import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import SEOHead from "@/components/SEOHead";
import {
  ShieldCheck,
  CheckCircle2,
  Phone,
  FileText,
  ArrowRight,
  HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  RelatedPages,
  allTreatmentsLinks,
  allDecisionLinks,
} from "@/components/RelatedPages";
import { Link } from "@/lib/router-compat";
export const AmeriHealthInsurance = () => {
  const logoUrl =
    "https://vibe.filesafe.space/1787327131284421342/attachments/e57c43d7-7693-4f2b-b394-404a67fcd4c4.jpg";
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={
          "AmeriHealth Insurance TMS & SPRAVATO® Coverage | Neuro Wellness FL"
        }
        description={
          "Learn how AmeriHealth Insurance policies cover TMS Therapy, SPRAVATO® esketamine, and advanced depression care at Neuro Wellness TMS in Coral Springs, FL."
        }
        canonical={"/amerihealth-insurance"}
        keywords={
          "AmeriHealth TMS coverage, AmeriHealth SPRAVATO authorization, AmeriHealth mental health benefits Coral Springs FL, AmeriHealth depression treatment Florida"
        }
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-navy text-primary-foreground py-16 sm:py-20 overflow-hidden">
          <div className="container-px relative mx-auto max-w-5xl text-center space-y-4">
            <span className="inline-block rounded-md bg-accent px-5 py-1.5 text-xs sm:text-sm font-bold text-white shadow-md uppercase tracking-wider">
              Insurance Partner & Coverage
            </span>
            <h1 className="font-headings text-3xl sm:text-5xl font-extrabold text-white">
              AmeriHealth Insurance Coverage
            </h1>
            <p className="text-base sm:text-lg text-primary-foreground/80 max-w-3xl mx-auto">
              Neuro Wellness TMS assists AmeriHealth policyholders in accessing
              advanced care for treatment-resistant depression, including TMS
              Therapy and SPRAVATO® esketamine.
            </p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-12 sm:py-16 bg-background">
          <div className="container-px mx-auto max-w-4xl space-y-10">
            {/* Logo & Overview Card */}
            <div className="rounded-2xl bg-card border border-border p-6 sm:p-10 shadow-soft space-y-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-6 border-b border-border pb-6">
                <div className="space-y-2 text-center sm:text-left">
                  <span className="text-xs font-semibold uppercase text-accent tracking-wider">
                    Insurance Carrier Guide
                  </span>
                  <h2 className="font-headings text-2xl font-bold text-primary">
                    AmeriHealth & Neuro Wellness TMS
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Coral Springs, FL · Serving Broward, Miami-Dade, & Palm
                    Beach Counties
                  </p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-border shadow-xs shrink-0 max-w-[220px] flex items-center justify-center">
                  <img
                    src={logoUrl}
                    alt="AmeriHealth Insurance Logo"
                    className="max-h-12 w-auto object-contain"
                  />
                </div>
              </div>

              <div className="space-y-4 text-foreground/85 text-base sm:text-lg leading-relaxed">
                <p>
                  If you carry an AmeriHealth insurance policy, you may have
                  benefits that cover non-invasive neuromodulation and
                  prescription interventional psychiatric treatments.
                </p>
                <p>
                  Navigating insurance requirements for specialized mental
                  health treatments can feel overwhelming. Our dedicated
                  financial coordination team manages the entire process for
                  you—from verifying your plan's specific policy options to
                  submitting prior authorization requests and managing any
                  required appeals.
                </p>
              </div>
            </div>

            {/* Coverage Breakdown */}
            <div className="rounded-2xl bg-card border border-border p-6 sm:p-10 shadow-soft space-y-6">
              <div className="flex items-center gap-3 text-primary">
                <ShieldCheck className="h-7 w-7 text-accent shrink-0" />
                <h2 className="font-headings text-2xl font-bold">
                  Treatments Covered Under AmeriHealth Policies
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                <div className="rounded-xl border border-border bg-muted/30 p-5 space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-accent shrink-0" />
                    <h3 className="font-headings text-base font-bold text-primary">
                      Standard TMS Therapy
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    FDA-cleared repetitive Transcranial Magnetic Stimulation
                    (rTMS) for adults with treatment-resistant major depressive
                    disorder who have not found adequate relief from oral
                    antidepressants.
                  </p>
                </div>

                <div className="rounded-xl border border-border bg-muted/30 p-5 space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-accent shrink-0" />
                    <h3 className="font-headings text-base font-bold text-primary">
                      SPRAVATO® (Esketamine)
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    FDA-approved prescription nasal spray administered at our
                    certified REMS treatment center under clinical supervision
                    for treatment-resistant depression.
                  </p>
                </div>

                <div className="rounded-xl border border-border bg-muted/30 p-5 space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-accent shrink-0" />
                    <h3 className="font-headings text-base font-bold text-primary">
                      Accelerated Protocols (SAINT®)
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    AmeriHealth and affiliated Blue Cross network plans have
                    options within specific policies for accelerated protocols.
                    Coverage is reviewed and determined case by case.
                  </p>
                </div>

                <div className="rounded-xl border border-border bg-muted/30 p-5 space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-accent shrink-0" />
                    <h3 className="font-headings text-base font-bold text-primary">
                      Medication & Psychotherapy
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Psychiatric evaluations, ongoing medication management, and
                    evidence-based psychotherapy sessions are widely covered
                    under outpatient behavioral health benefits.
                  </p>
                </div>
              </div>
            </div>

            {/* Step-by-Step Sequence */}
            <div className="rounded-2xl bg-card border border-border p-6 sm:p-10 shadow-soft space-y-6">
              <div className="flex items-center gap-3 text-primary">
                <FileText className="h-7 w-7 text-accent shrink-0" />
                <h2 className="font-headings text-2xl font-bold">
                  How We Verify Your Benefits
                </h2>
              </div>

              <ol className="space-y-4">
                <li className="flex gap-4 items-start">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-white text-xs font-bold">
                    1
                  </span>
                  <div>
                    <h3 className="font-bold text-primary text-base">
                      Free Benefit Check
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Provide your AmeriHealth card details during your initial
                      phone consultation. We verify your exact outpatient mental
                      health copays, deductibles, and prior authorization
                      requirements.
                    </p>
                  </div>
                </li>
                <li className="flex gap-4 items-start">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-white text-xs font-bold">
                    2
                  </span>
                  <div>
                    <h3 className="font-bold text-primary text-base">
                      Clinical Consultation & Record Gathering
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Our clinical team evaluates your medical history and
                      gathers documentation of prior antidepressant trials
                      required by AmeriHealth's medical necessity guidelines.
                    </p>
                  </div>
                </li>
                <li className="flex gap-4 items-start">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-white text-xs font-bold">
                    3
                  </span>
                  <div>
                    <h3 className="font-bold text-primary text-base">
                      Prior Authorization & Pre-Treatment Pricing
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      We submit formal authorization requests and handle appeals
                      if necessary. We confirm your out-of-pocket obligation in
                      writing before any care begins.
                    </p>
                  </div>
                </li>
              </ol>

              <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <p className="font-bold text-primary">
                    Ready to verify your AmeriHealth coverage?
                  </p>
                  <p className="text-xs text-muted-foreground">
                    No obligation. Our benefits team will answer all your
                    questions.
                  </p>
                </div>
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90 shrink-0"
                >
                  <a href="tel:9548279793">
                    <Phone className="h-4 w-4 mr-2" />
                    Call (954) 827-9793
                  </a>
                </Button>
              </div>
            </div>

            {/* Notice banner */}
            <div className="rounded-xl border border-muted bg-muted/40 p-5 space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2 font-semibold text-primary">
                <HelpCircle className="h-4 w-4 text-accent shrink-0" />
                <span>Important Note on Coverage Determination</span>
              </div>
              <p>
                Coverage varies by individual plan and state of issue.
                Verification of benefits is not a guarantee of payment; final
                approval depends on medical necessity review by AmeriHealth.
                Neuro Wellness TMS submits all necessary clinical documentation
                on your behalf.
              </p>
            </div>
          </div>
        </section>

        <RelatedPages
          heading="Explore Related Insurance & Treatment Resources"
          links={[
            {
              title: "How Insurance Actually Works Here",
              href: "/how-insurance-works",
              description: "Understanding verification vs. prior authorization",
            },
            {
              title: "Cost & Insurance Overview",
              href: "/cost-insurance",
              description: "All accepted insurance providers & financing",
            },
            {
              title: "Financing Options & CareCredit",
              href: "/contents/patient-info/financing-options",
              description: "Flexible payment plans",
            },
            ...allTreatmentsLinks.slice(0, 3),
            ...allDecisionLinks.slice(0, 3),
          ]}
        />

        <Consultation />
      </main>
      <Footer />
    </div>
  );
};
export default AmeriHealthInsurance;
